import { useState, useCallback, useRef } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { InventoryItem } from '@/types/inventory';
import { searchInventoryFast, clearFastSearchCache } from '@/services/search/fastInventorySearch';

export type SearchDependencies = {
  lastUpdate: string | null;
  setLastUpdate: (value: string | null) => void;
};

export function useInventorySearch(dependencies: SearchDependencies) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { lastUpdate, setLastUpdate } = dependencies;
  
  // Track last search to prevent duplicate requests
  const lastSearchRef = useRef<{ key: string; ts: number }>({ key: '', ts: 0 });
  
  // Store last results in ref for duplicate check (avoids dependency on results state)
  const lastResultsRef = useRef<InventoryItem[]>([]);
  
  // AbortController for canceling stale requests
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Reduced debounce from 300ms to 150ms for faster response
  const debouncedSearch = useDebounce(searchTerm, 150);

  // Fast search function using optimized PostgreSQL function
  const performSearch = useCallback(async (term: string, warehouseId: number | null = null) => {
    // Clean and validate input
    const cleanTerm = term.trim();
    const searchKey = `${cleanTerm}-${warehouseId}`;
    const now = Date.now();
    
    // Skip fast duplicate searches (use ref to avoid dependency on results state)
    if (
      searchKey === lastSearchRef.current.key &&
      now - lastSearchRef.current.ts < 500 &&
      lastResultsRef.current.length > 0
    ) {
      console.log('🔄 Skipping duplicate search:', searchKey);
      return lastResultsRef.current;
    }

    // Don't search if term is too short and no warehouse filter
    if (cleanTerm.length < 1 && warehouseId === null) {
      setResults([]);
      setHasSearched(false);
      return [];
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setHasSearched(true);
    lastSearchRef.current = { key: searchKey, ts: now };
    
    try {
      console.log(`🚀 Fast search: "${cleanTerm}" ${warehouseId ? `in magazijn ${warehouseId}` : ''}`);
      
      const { results: searchResults, lastUpdate: timestamp } = await searchInventoryFast(
        cleanTerm, 
        warehouseId,
        150 // Max results
      );
      
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        console.log('🛑 Search request aborted');
        return [];
      }
      
      // Update last update timestamp
      if (timestamp) {
        setLastUpdate(timestamp);
      }
      
      setResults(searchResults);
      lastResultsRef.current = searchResults;

      return searchResults;
      
    } catch (error) {
      // Don't show error for aborted requests
      if (error instanceof Error && error.name === 'AbortError') {
        return [];
      }
      console.error('Fast search error:', error);
      setResults([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [setLastUpdate]);

  // Clear search results and cache
  const clearResults = useCallback(() => {
    setResults([]);
    lastResultsRef.current = [];
    setHasSearched(false);
    lastSearchRef.current = { key: '', ts: 0 };
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Force refresh - clears cache and re-searches
  const refreshSearch = useCallback(async (warehouseId: number | null = null) => {
    clearFastSearchCache();
    lastSearchRef.current = { key: '', ts: 0 };
    if (searchTerm.trim().length >= 1 || warehouseId !== null) {
      return performSearch(searchTerm, warehouseId);
    }
    return [];
  }, [searchTerm, performSearch]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    results,
    setResults,
    isLoading,
    hasSearched,
    debouncedSearch,
    lastUpdate,
    
    // Actions
    performSearch,
    clearResults,
    refreshSearch
  };
}
